// Pure payload builder for the manual "Add grant" form (AdminVerify).
// Kept free of React/DOM so it is unit-testable in plain vitest, and so the
// validation messages live in one place.

export interface ManualGrantFormState {
  funder_id: string;
  title: string;
  source_url: string;
  description: string;
  funding_min_cad: string;   // raw input strings; '' = not provided
  funding_max_cad: string;
  closes_at_date: string;    // YYYY-MM-DD from <input type="date">, '' = none
  intake_type: '' | 'continuous' | 'window' | 'invitational' | 'closed';
  match_required: boolean;
}

export const EMPTY_MANUAL_GRANT_FORM: ManualGrantFormState = {
  funder_id: '',
  title: '',
  source_url: '',
  description: '',
  funding_min_cad: '',
  funding_max_cad: '',
  closes_at_date: '',
  intake_type: '',
  match_required: false,
};

export type ManualGrantBuildResult =
  | { ok: true; payload: Record<string, unknown> }
  | { ok: false; errors: string[] };

/** End-of-day on the given calendar date, in the BROWSER's timezone (the
 *  reviewer is in Eastern Time; entering "the deadline date" and meaning
 *  end-of-day matches how funders phrase deadlines). */
export function dateInputToEndOfDayMs(yyyyMmDd: string): number | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(yyyyMmDd)) return null;
  const d = new Date(`${yyyyMmDd}T23:59:59`);
  const ms = d.getTime();
  return Number.isFinite(ms) ? ms : null;
}

export function buildManualGrantPayload(form: ManualGrantFormState): ManualGrantBuildResult {
  const errors: string[] = [];
  const funder_id = form.funder_id.trim();
  const title = form.title.trim();
  const source_url = form.source_url.trim();
  const description = form.description.trim();

  if (!funder_id) errors.push('Pick the funder.');
  if (!title) errors.push('Title is required.');
  if (!source_url) {
    errors.push('Source URL is required — every grant points at a funder page.');
  } else {
    try {
      const u = new URL(source_url);
      if (u.protocol !== 'https:' && u.protocol !== 'http:') errors.push('Source URL must be a web address (http/https).');
    } catch {
      errors.push('Source URL is not a valid web address.');
    }
  }
  if (!description) errors.push('Add a one-line description (what is this funding for?).');

  const min = parseCad(form.funding_min_cad);
  const max = parseCad(form.funding_max_cad);
  if (min.error) errors.push('Funding min must be a non-negative whole dollar amount.');
  if (max.error) errors.push('Funding max must be a non-negative whole dollar amount.');
  if (min.value !== null && max.value !== null && min.value > max.value) {
    errors.push('Funding min is larger than funding max.');
  }

  let closes_at: number | null = null;
  if (form.closes_at_date) {
    closes_at = dateInputToEndOfDayMs(form.closes_at_date);
    if (closes_at === null) errors.push('Deadline date is not valid.');
  }

  if (errors.length > 0) return { ok: false, errors };

  const payload: Record<string, unknown> = {
    funder_id,
    title,
    source_url,
    description,
    match_required: form.match_required,
  };
  if (min.value !== null) payload.funding_min_cad = min.value;
  if (max.value !== null) payload.funding_max_cad = max.value;
  if (closes_at !== null) payload.closes_at = closes_at;
  if (form.intake_type) payload.intake_type = form.intake_type;
  return { ok: true, payload };
}

function parseCad(raw: string): { value: number | null; error: boolean } {
  const s = raw.replace(/[$,\s]/g, '');
  if (s === '') return { value: null, error: false };
  const n = Number(s);
  if (!Number.isFinite(n) || n < 0 || !Number.isInteger(n)) return { value: null, error: true };
  return { value: n, error: false };
}
